/**
 * Datetime filter translation utility
 *
 * Converts SimpleTable datetime filter values to GraphQL filter format
 * Handles both preset filters and custom ranges
 *
 * TIMEZONE CONTRACT: storage is UTC (`timestamptz`, ISO `…Z`) but every bound
 * here is built from a LOCAL wall-clock calendar day — the same convention
 * `datetime-presets` uses via date-fns. On the client that local zone is the
 * rep's own zone (Central for WTC), which is what "this month" has to mean on
 * screen. A date-only string ("2026-03-31") carries no time and no zone, so it
 * MUST NOT go through `new Date(str)` — that reads it as UTC midnight, which in
 * a negative-offset zone is the PREVIOUS calendar day. See WTC_DEFAULT_TIMEZONE.
 */

import {
  getDatetimePreset,
  isCustomRange,
  parseCustomRange,
  NEVER_LAST_ACTIVITY_PRESET_VALUE,
  NEVER_LAST_ACTIVITY_PRESET_LABEL,
} from "./datetime-presets";

/** The GraphQL comparison payload this module emits for a single field. */
export type DatetimeFieldFilter =
  | { gte: string; lte: string }
  | { gte: string }
  | { lte: string }
  | { is: "NULL" | "NOT_NULL" };

/** An "advanced" filter as stored in table/saved-view state: an untrusted envelope. */
export interface AdvancedFilterEnvelope {
  type: string;
  value: unknown;
}

export type DatetimeFilterValue = string | AdvancedFilterEnvelope;

/** `YYYY-MM-DD` — what an `<input type="date">` and a Postgres `date` column produce. */
const DATE_ONLY_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

type DateInput = string | number | Date;

function isDateInput(value: unknown): value is DateInput {
  return typeof value === "string" || typeof value === "number" || value instanceof Date;
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return typeof value === "object" && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

/** Coerce a numeric-ish field (year/month/day/days) the way `new Date(...)` used to. */
function asPositiveNumber(value: unknown): number | null {
  const n = typeof value === "number" ? value : typeof value === "string" ? Number(value) : NaN;
  return Number.isFinite(n) && n > 0 ? n : null;
}

function isValidDate(date: Date): boolean {
  return !Number.isNaN(date.getTime());
}

/** Last representable instant of a local day — inclusive `lte` bounds must reach `.999`. */
function endOfLocalDay(year: number, monthIndex: number, day: number): Date {
  return new Date(year, monthIndex, day, 23, 59, 59, 999);
}

/**
 * Resolve one side of a date-range bound to a concrete instant.
 *
 * `edge: "end"` widens a DATE-ONLY input to the last instant of that local day.
 * Without it `lte: "2026-03-31"` becomes `2026-03-31T00:00:00.000Z` and the
 * rep's "Mar 1 – Mar 31" filter silently hides everything that happened ON
 * Mar 31. A value that already carries a time (full ISO string, epoch ms, Date)
 * is an exact instant and is used verbatim.
 */
function resolveBoundary(value: DateInput, edge: "start" | "end"): Date | null {
  if (typeof value === "string" && DATE_ONLY_PATTERN.test(value)) {
    const [year, month, day] = value.split("-").map(Number);
    const local =
      edge === "end" ? endOfLocalDay(year, month - 1, day) : new Date(year, month - 1, day);
    return isValidDate(local) ? local : null;
  }

  const parsed = value instanceof Date ? new Date(value.getTime()) : new Date(value);
  return isValidDate(parsed) ? parsed : null;
}

/**
 * Translate a datetime filter value to GraphQL format
 */
export const translateDatetimeFilter = (
  fieldId: string,
  filterValue: DatetimeFilterValue
): Record<string, DatetimeFieldFilter> => {
  // Handle simple preset filters (string values)
  if (typeof filterValue === "string") {
    if (filterValue === NEVER_LAST_ACTIVITY_PRESET_VALUE) {
      return { [fieldId]: { is: "NULL" } };
    }

    // Handle custom range (stored as JSON string)
    if (isCustomRange(filterValue)) {
      const range = parseCustomRange(filterValue);
      if (range) {
        return {
          [fieldId]: {
            gte: range.start.toISOString(),
            lte: range.end.toISOString(),
          },
        };
      }
    }

    // Handle preset filters
    const preset = getDatetimePreset(filterValue);
    if (preset) {
      const { start, end } = preset.getDateRange();
      return {
        [fieldId]: {
          gte: start.toISOString(),
          lte: end.toISOString(),
        },
      };
    }
  }

  // Handle advanced filters (object with type)
  if (typeof filterValue === "object" && filterValue !== null && filterValue.type) {
    return translateAdvancedDatetimeFilter(fieldId, filterValue);
  }

  return {};
};

/**
 * Translate advanced datetime filter to GraphQL format
 */
const translateAdvancedDatetimeFilter = (
  fieldId: string,
  filter: AdvancedFilterEnvelope
): Record<string, DatetimeFieldFilter> => {
  const { type, value } = filter;
  const record = asRecord(value);

  switch (type) {
    case "dateRange": {
      // Standard date range filter. Either side may be omitted — a one-sided
      // range is a real filter (the chip renders "From …"/"Until …"), so
      // dropping it would show the rep an unfiltered list under an active chip.
      const rawMin = record?.min;
      const rawMax = record?.max;
      const start = isDateInput(rawMin) && rawMin !== "" ? resolveBoundary(rawMin, "start") : null;
      const end = isDateInput(rawMax) && rawMax !== "" ? resolveBoundary(rawMax, "end") : null;

      if (start && end) {
        return { [fieldId]: { gte: start.toISOString(), lte: end.toISOString() } };
      }
      if (start) return { [fieldId]: { gte: start.toISOString() } };
      if (end) return { [fieldId]: { lte: end.toISOString() } };
      break;
    }

    case "dateMonth": {
      // Specific month filter
      const month = asPositiveNumber(record?.month);
      const year = asPositiveNumber(record?.year);
      if (month !== null && year !== null) {
        const startOfMonth = new Date(year, month - 1, 1);
        const endOfMonth = endOfLocalDay(year, month, 0);
        return {
          [fieldId]: {
            gte: startOfMonth.toISOString(),
            lte: endOfMonth.toISOString(),
          },
        };
      }
      break;
    }

    case "dateYear": {
      // Specific year filter
      const year = asPositiveNumber(value);
      if (year !== null) {
        const startOfYear = new Date(year, 0, 1);
        const endOfYear = endOfLocalDay(year, 11, 31);
        return {
          [fieldId]: {
            gte: startOfYear.toISOString(),
            lte: endOfYear.toISOString(),
          },
        };
      }
      break;
    }

    case "dateDay": {
      // Specific day filter
      const day = asPositiveNumber(record?.day);
      const month = asPositiveNumber(record?.month);
      const year = asPositiveNumber(record?.year);
      if (day !== null && month !== null && year !== null) {
        const startOfDay = new Date(year, month - 1, day);
        const endOfDay = endOfLocalDay(year, month - 1, day);
        return {
          [fieldId]: {
            gte: startOfDay.toISOString(),
            lte: endOfDay.toISOString(),
          },
        };
      }
      break;
    }

    case "dateMonthRange": {
      // Month range filter
      const startMonth = asPositiveNumber(record?.startMonth);
      const startYear = asPositiveNumber(record?.startYear);
      const endMonth = asPositiveNumber(record?.endMonth);
      const endYear = asPositiveNumber(record?.endYear);
      if (startMonth !== null && startYear !== null && endMonth !== null && endYear !== null) {
        const start = new Date(startYear, startMonth - 1, 1);
        const end = endOfLocalDay(endYear, endMonth, 0);
        return {
          [fieldId]: {
            gte: start.toISOString(),
            lte: end.toISOString(),
          },
        };
      }
      break;
    }

    case "dateYearRange": {
      // Year range filter
      const startYear = asPositiveNumber(record?.startYear);
      const endYear = asPositiveNumber(record?.endYear);
      if (startYear !== null && endYear !== null) {
        const start = new Date(startYear, 0, 1);
        const end = endOfLocalDay(endYear, 11, 31);
        return {
          [fieldId]: {
            gte: start.toISOString(),
            lte: end.toISOString(),
          },
        };
      }
      break;
    }

    case "relativeDate": {
      // Relative date filter (e.g., "3 days ago")
      const days = asPositiveNumber(record?.days);
      if (days !== null) {
        const end = new Date();
        const start = new Date();
        start.setDate(start.getDate() - days);
        return {
          [fieldId]: {
            gte: start.toISOString(),
            lte: end.toISOString(),
          },
        };
      }
      break;
    }
  }

  return {};
};

/**
 * Build GraphQL filters from SimpleTable filter state
 *
 * CAUTION: every plain STRING value is routed through the datetime translator,
 * so this must only be handed fields that actually opt into datetime presets
 * (`fieldConfig.datetimePresets.enabled`) — see `lib/services/filter-conversion`.
 * A string that resolves to no known preset yields NO clause at all (the filter
 * is silently dropped, widening the result set), which is only acceptable
 * because the sole caller has already established the field is a date field.
 */
export const buildGraphQLFilters = (
  filters: Record<string, unknown>
): Record<string, unknown> => {
  const graphqlFilters: Record<string, unknown> = {};

  Object.entries(filters).forEach(([fieldId, filterValue]) => {
    const envelope = asRecord(filterValue);

    // Handle notNull filter
    if (envelope?.type === "notNull") {
      graphqlFilters[fieldId] = { is: "NOT_NULL" };
    }
    // Check if this is a datetime filter by checking if it's a string (preset) or has datetime type
    else if (
      typeof filterValue === "string" ||
      (typeof envelope?.type === "string" && envelope.type.startsWith("date"))
    ) {
      const translatedFilter = translateDatetimeFilter(
        fieldId,
        typeof filterValue === "string"
          ? filterValue
          : { type: String(envelope?.type), value: envelope?.value }
      );
      Object.assign(graphqlFilters, translatedFilter);
    } else {
      // Handle other filter types (existing logic)
      if (filterValue && filterValue !== "all") {
        graphqlFilters[fieldId] = { eq: filterValue };
      }
    }
  });

  return graphqlFilters;
};

/**
 * Check if a field should use datetime filtering
 */
export const isDatetimeField = (fieldConfig: { type?: string } | null | undefined): boolean => {
  return fieldConfig?.type === "datetime" || fieldConfig?.type === "date";
};

/**
 * Get the appropriate filter value for display
 */
export const getFilterDisplayValue = (filterValue: unknown): string => {
  if (typeof filterValue === "string") {
    if (filterValue === NEVER_LAST_ACTIVITY_PRESET_VALUE) {
      return NEVER_LAST_ACTIVITY_PRESET_LABEL;
    }

    if (isCustomRange(filterValue)) {
      const range = parseCustomRange(filterValue);
      if (range) {
        return `Custom: ${range.start.toLocaleDateString()} - ${range.end.toLocaleDateString()}`;
      }
      return "Custom range";
    }

    const preset = getDatetimePreset(filterValue);
    return preset ? preset.label : filterValue;
  }

  const envelope = asRecord(filterValue);
  if (envelope?.type) {
    return `${String(envelope.type)}: ${JSON.stringify(envelope.value)}`;
  }

  return String(filterValue);
};
